import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { fetchTenantIdentidade, type TenantIdentidade } from '@/api/tenantApi'
import { getTenantSlug, TENANT_SLUG_CHANGED_EVENT } from '@/config/tenant'
import { resolveMediaUrl } from '@/utils/mediaUrl'

const STORAGE_NOME_KEY = 'agendamentos-app:tenant-nome'

type TenantContextValue = {
  slug: string
  identidade: TenantIdentidade | null
  nome: string
  logoUrl: string | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  setCachedNome: (nome: string) => void
}

const TenantContext = createContext<TenantContextValue | null>(null)

function loadCachedNome(): string | null {
  try {
    const n = localStorage.getItem(STORAGE_NOME_KEY)?.trim()
    return n || null
  } catch {
    return null
  }
}

function saveCachedNome(nome: string): void {
  try {
    localStorage.setItem(STORAGE_NOME_KEY, nome.trim())
  } catch {
    /* ignore */
  }
}

export function clearCachedTenantNome(): void {
  try {
    localStorage.removeItem(STORAGE_NOME_KEY)
  } catch {
    /* ignore */
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [slug, setSlug] = useState(getTenantSlug)
  const [identidade, setIdentidade] = useState<TenantIdentidade | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cachedNome, setCachedNomeState] = useState<string | null>(loadCachedNome)

  const refresh = useCallback(async () => {
    const current = getTenantSlug()
    setSlug(current)
    if (!current) {
      setIdentidade(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchTenantIdentidade(current)
      setIdentidade(data)
      saveCachedNome(data.nome)
      setCachedNomeState(data.nome)
    } catch (e) {
      setIdentidade(null)
      setError(e instanceof Error ? e.message : 'Não foi possível carregar a empresa.')
    } finally {
      setLoading(false)
    }
  }, [])

  const setCachedNome = useCallback((nome: string) => {
    saveCachedNome(nome)
    setCachedNomeState(nome.trim())
    setIdentidade((prev) =>
      prev ? { ...prev, nome: nome.trim() } : prev
    )
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onSlugChange = () => void refresh()
    window.addEventListener(TENANT_SLUG_CHANGED_EVENT, onSlugChange)
    return () => window.removeEventListener(TENANT_SLUG_CHANGED_EVENT, onSlugChange)
  }, [refresh])

  const nome = identidade?.nome ?? cachedNome ?? (slug || 'Empresa')
  const logoUrl = identidade?.logoUrl
    ? resolveMediaUrl(identidade.logoUrl)
    : null

  const value = useMemo(
    () => ({
      slug,
      identidade,
      nome,
      logoUrl,
      loading,
      error,
      refresh,
      setCachedNome,
    }),
    [slug, identidade, nome, logoUrl, loading, error, refresh, setCachedNome]
  )

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant deve ser usado dentro de TenantProvider')
  return ctx
}
